import { supabase } from '@/integrations/supabase/client';

/**
 * Clear all Supabase and auth-related storage completely
 */
export const clearAuthStorage = async () => {
  try {
    // Sign out from Supabase first
    await supabase.auth.signOut();
    
    // Clear localStorage items related to auth
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes('supabase') || key.includes('sb-') || key.includes('auth') || key === 'sb-auth-conflict-check')) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });
    
    // Clear session storage
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith('sb-') || key.includes('supabase') || key.includes('auth')) {
        sessionStorage.removeItem(key);
      }
    });
    
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Check for auth conflicts without aggressive clearing
 */
export const checkAuthConflicts = async () => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    // If there's no session, clear any stale conflict check data
    if (!session) {
      localStorage.removeItem('sb-auth-conflict-check');
      return false;
    }
    
    const storedUser = localStorage.getItem('sb-auth-conflict-check');
    
    if (storedUser && storedUser !== JSON.stringify(session.user)) {
      await clearAuthStorage();
      return true; // Conflict was resolved
    }
    
    // Update stored user for future conflict detection
    localStorage.setItem('sb-auth-conflict-check', JSON.stringify(session.user));
    return false; // No conflict
  } catch (error) {
    return false;
  }
};

/**
 * Initialize auth state with conflict detection
 */
export const initializeAuthState = async () => {
  try {
    // Clear any stale data first
    await clearAuthStorage();
    
    // Get current session
    const { data: { session } } = await supabase.auth.getSession();
    
    if (session) {
      // Store current user for conflict detection
      localStorage.setItem('sb-auth-conflict-check', JSON.stringify(session.user));
      return session;
    }
    
    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Safe sign up with conflict prevention
 */
export const safeSignUp = async (email: string, password: string, displayName?: string, redirectTo?: string) => {
  try {
    // Clear any existing session first
    await supabase.auth.signOut();
    
    // Don't use emailRedirectTo if it's an invitation URL
    const options: any = {
      data: {
        display_name: displayName || email.split('@')[0]
      }
    };
    
    // Only set emailRedirectTo if it's not an invitation URL
    if (redirectTo && !redirectTo.includes('/invite/')) {
      options.emailRedirectTo = redirectTo;
    }
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options
    });
    
    return { data, error };
  } catch (error) {
    return { data: null, error };
  }
};

/**
 * Safe sign in with conflict prevention
 */
export const safeSignIn = async (email: string, password: string) => {
  try {
    // Clear any existing session first
    await supabase.auth.signOut();
    
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (!error && data.session) {
      // Store current user for conflict detection
      localStorage.setItem('sb-auth-conflict-check', JSON.stringify(data.user));
    }
    
    return { data, error };
  } catch (error) {
    return { data: null, error };
  }
}; 